import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "react-router-dom";

import { refusalText } from "../api/errors";
import { useApiCommand } from "../api/hooks";
import type { SuggestionsBlock } from "../api/types";
import { agentContextFor } from "../app/screenRoutes";
import { PromptChip } from "../ui/Chip";
import { SafeMarkdown } from "../ui/SafeMarkdown";
import { conversationQueryKey } from "./conversation";
import type { BlockProps } from "./registry";

import "./chatbar.css";

/**
 * Prompts the agent suggests (rebuild 4.0), drawn as chips under its words. A chip sends its words to the same conversation,
 * as if the person had typed them, with the screen's context. It sends text and nothing else: no chip accepts, validates or
 * declines a card (card by card, no accept-all). Once one is sent, the row is spent, so a second press does not send twice.
 */
export function SuggestionsBlockView({ block, context }: BlockProps<SuggestionsBlock>) {
  const queryClient = useQueryClient();
  const { pathname, search } = useLocation();
  const postMessage = useApiCommand("post_message");
  const postAdminMessage = useApiCommand("post_admin_message");
  const [sent, setSent] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const busy = postMessage.isPending || postAdminMessage.isPending;

  if (context.role === "user" || block.options.length === 0) return null;

  const send = async (text: string) => {
    if (busy || sent) return;
    setProblem(null);
    try {
      const input = { key: context.conversationKey, text, context: agentContextFor(pathname, search) };
      // The admin's conversations have their own path (the admin has no discipline).
      if (context.admin) await postAdminMessage.mutateAsync(input);
      else await postMessage.mutateAsync(input);
      setSent(text);
      void queryClient.invalidateQueries({ queryKey: conversationQueryKey(context.conversationKey, context.admin) });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <section className="agent-suggestions" aria-label="Suggested questions">
      {block.intro && (
        <div className="agent-text" data-agent="true">
          <SafeMarkdown>{block.intro}</SafeMarkdown>
        </div>
      )}
      <div className="agent-suggestions__chips" role="group" aria-label="Send one of these">
        {block.options.map((option) => (
          <PromptChip key={option} disabled={busy || sent !== null || context.live} data-sent={sent === option ? "true" : undefined} onClick={() => void send(option)}>
            {option}
          </PromptChip>
        ))}
      </div>
      {problem && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}
    </section>
  );
}
